/** "Retry job creation" releases a quarantined Visit back to the Job stamper: record surfaces, board row menu and board bulk. */
import { computed, inject, Injector } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, VisitBrowseCommandId, VisitBrowseRow, visitBrowseCapabilities } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';
import { visitSurfaceModel } from '../visit.model';
import { MAX_VISIT_OVERDUE_BULK, runVisitsPerRow } from './visit.overdue.bulk.command';

const LABEL = 'Retry job creation';
const ICON = 'fa-regular fa-arrows-rotate';
const QUEUED = 'Job creation queued';
export const VISIT_RETRY_JOB_CREATION_BULK_ID = 'visit.retryJobCreation';

type RetryVisits = (rows: readonly VisitBrowseRow[]) => Promise<boolean>;

export function visitRetryJobCreationCommand(hostReadonly = false): ActionCommandCapability<VisitBrowseRow> {

  const rights = inject(RightsService);
  const data = inject(EntityDataService);
  // Resolved at run time: the surface factory mounts in hosts that never retry, so the toast outlet is not a mount dependency.
  const injector = inject(Injector);
  // Not gated on the page's readonly: a quarantined Visit is often locked for editing, yet retrying writes none of its fields.
  const allowed = (row: VisitBrowseRow) => !hostReadonly && retryable(row) && canRetry(rights);
  return {
    descriptor: ({ page }) => computed(() => ({ id: 'retryJobCreation', label: LABEL, iconLeft: ICON, pin: 'preferred', hidden: !allowed(page.$model()), disabled: page.$saving() || page.$dirty() })),
    run: async page => {
      const row = page.$model();
      if (!allowed(row) || page.$saving() || page.$dirty()) return;
      const updated = await retryJobCreation(data, row);
      if (!updated?._id) throw new Error('The Visit command did not return the updated Visit');
      page.reset({ ...visitSurfaceModel(updated) });
      injector.get(ToastService).success(LABEL, QUEUED);
      data.invalidate(ActivityEntityName.Visit);
      await data.invalidateItems(ActivityEntityName.UpcomingWork);
    },
  };
}

/** Board row menu. The row's own attention overlay decides; the domain re-reads it before queuing. */
export function visitRetryJobCreationRowCommands(row: VisitBrowseRow): Command[] {

  if (!retryable(row) || !canRetry(inject(RightsService))) return [];
  const retry = boardRetrier();
  return [new Command({ _id: 'retryJobCreation', label: LABEL, iconLeft: ICON, action: () => { void retry([row]); } })];
}

export function visitRetryJobCreationBulkCommands(args: EntityBoardBulkCommandsArgs<VisitBrowseRow>): Command[] {

  const rows = (args.selectedItems ?? []).filter(retryable);
  if (!withinCap(rows) || !canRetry(inject(RightsService))) return [];
  return [new Command({ _id: VISIT_RETRY_JOB_CREATION_BULK_ID, label: LABEL, iconLeft: ICON, data: { run: boardRetrier() } satisfies { run: RetryVisits } })];
}

export async function runVisitRetryJobCreationBulk(args: BulkCommandArgs<VisitBrowseRow>): Promise<BulkCommandOutcome | false> {

  const run = (args.commandData as { run?: RetryVisits } | undefined)?.run;
  if (args.action !== VISIT_RETRY_JOB_CREATION_BULK_ID || !run) return false;
  const rows = args.data.filter(retryable);
  if (!withinCap(rows)) return { handled: true, clearSelection: false };
  return { handled: true, clearSelection: await run(rows) };
}

function retryable(row: VisitBrowseRow): boolean {

  return visitBrowseCapabilities(row).retryJobCreation;
}

/** Mirrors the strategy: queuing a Job build is a dispatch decision, not only a Visit edit. */
function canRetry(rights: RightsService): boolean {

  return rights.hasRights(JobSecurityRight.CanAccess) && rights.hasRights(JobSecurityRight.Manage) && rights.hasRights(JobSecurityRight.Dispatch);
}

function withinCap(rows: readonly VisitBrowseRow[]): boolean {

  return rows.length > 0 && rows.length <= MAX_VISIT_OVERDUE_BULK;
}

function retryJobCreation(data: EntityDataService, row: VisitBrowseRow): Promise<VisitBrowseRow> {

  return data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VisitBrowseCommandId.RetryJobCreation, { id: row.visitId ?? row._id });
}

/** Stamped in the board's injection context; the menu action and bulk handler run outside it. */
function boardRetrier(): RetryVisits {

  const injector = inject(Injector);
  return rows => runVisitsPerRow(injector, rows, {
    title: LABEL,
    done: count => (rows.length === 1 ? QUEUED : `${QUEUED} for ${count}`),
    run: async (data, row) => {
      await retryJobCreation(data, row);
    },
  });
}
